import type {
  BulkImportResult,
  BulkLimits,
  BulkPreview,
  BulkRowBase,
} from "../../../shared/bulkUpload";

/** The colors a geofence can be drawn with, as the Create screen offers them. */
export const GEOFENCE_COLORS = [
  "PURPLE",
  "GREEN",
  "ORANGE",
  "RED",
  "PINK",
  "BLUE",
  "GRAY",
  "GOLD",
] as const;

export type GeofenceColor = (typeof GEOFENCE_COLORS)[number];

/** Scenario 18: a geofence with no color picked takes this one. The design's
 *  purple, not the story's blue. */
export const DEFAULT_GEOFENCE_COLOR: GeofenceColor = "PURPLE";

/** What each color paints, on the map and on the list. */
export const GEOFENCE_COLOR_VALUES: Readonly<Record<GeofenceColor, string>> = {
  PURPLE: "#4d34ae",
  GREEN: "#17874a",
  ORANGE: "#f5411a",
  RED: "#d50b3e",
  PINK: "#e011b4",
  BLUE: "#0b77b3",
  GRAY: "#8c8ca6",
  GOLD: "#c08a0a",
};

/** Scenario 13: a circle's radius, in meters. */
export const GEOFENCE_RADIUS_MIN = 1;
export const GEOFENCE_RADIUS_MAX = 50_000;
export const GEOFENCE_NAME_MAX = 150;
/** Fewer points can't close an area. */
export const GEOFENCE_POLYGON_MIN_POINTS = 3;

export interface GeofencePoint {
  readonly latitude: number;
  readonly longitude: number;
}

/** A geofence's area: one definition only, never both (Scenario 16). */
export type GeofenceShape =
  | { readonly kind: "POLYGON"; readonly points: readonly GeofencePoint[] }
  | {
      readonly kind: "CIRCLE";
      readonly center: GeofencePoint;
      readonly radiusMeters: number;
    };

/** TEMPORARY: a geofence as the screens use it, until the API defines one. */
export interface Geofence {
  readonly id: string;
  /** The short code on its card, shown as `#49234`. */
  readonly code: string;
  readonly name: string;
  readonly color: GeofenceColor;
  readonly shape: GeofenceShape;
}

/** One page of the geofence list, newest first. */
export interface GeofenceListQuery {
  readonly page: number;
  readonly limit: number;
  /** Part of a geofence name, any case; empty or left out for every one. */
  readonly search?: string;
}

/** Download on the list: every geofence the search matches, in one Excel file. */
export interface GeofenceExportQuery {
  readonly search?: string;
}

/** Every geofence at once, for the map layer. */
export interface GeofenceMapQuery {
  readonly search?: string;
}

/** A new geofence. It goes into the logged-in user's own company: there's no
 *  company to send (Scenario 3). */
export interface CreateGeofenceInput {
  readonly name: string;
  readonly color: GeofenceColor;
  readonly shape: GeofenceShape;
}

/** A geofence's new values, all of them. Its id, code and company never change
 *  (Edit Geofence, Scenarios 1 and 3). */
export type UpdateGeofenceInput = CreateGeofenceInput;

/** One row of an uploaded sheet, its values as read (so an invalid one may hold
 *  a coordinate that isn't a number). TEMPORARY, as the API has no geofences. */
export interface GeofenceBulkRow extends BulkRowBase {
  readonly name: string | null;
  readonly latitude: number | string | null;
  readonly longitude: number | string | null;
  readonly radius: number | string | null;
}

export type GeofenceBulkPreview = BulkPreview<GeofenceBulkRow>;
/** Bulk Upload's limits: one `.xlsx` of at most 20 MB holding at most 5,000
 *  records. */
export const GEOFENCE_BULK_LIMITS: BulkLimits = {
  fileExtension: ".xlsx",
  maxFileMb: 20,
  maxRecords: 5000,
};

export type GeofenceBulkImportResult = BulkImportResult<GeofenceBulkRow>;

/** A saved geofence, with the server's success message if it sends one. */
export interface SavedGeofence {
  readonly geofence: Geofence;
  readonly message?: string;
}
